import {
  SavedFormQuestion,
  SavedFormQuestionState,
  SelectRecipientGroupType,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import filter from "lodash.filter";
import { Colors } from "../../../../../utils/colors";
import FlatList from "flatlist-react/lib";
import { sortSavedQuestions } from "@markit/common.utils";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { Icon } from "@iconify/react";
import SavedQuestionItem from "../../../../DisplayItem/DataQuestion/SavedQuestionItem";
import { EmptyStateFlatlist } from "../../../../EmptyStates/EmptyStateFlatlist";
import EmptyStateButton from "../../../../Buttons/EmptyStateButton";
import SavedQuestionTypeDropdown, {
  DataQuestionFilterType,
} from "../../../../Form/SavedQuestionTypeDropdown";
import ListTabs from "../../../../DropdownsAndTabs/ListTabs";
import { getSavedFormQuestionsToDisplay } from "../../../../../utils/eventUtils/formUtils";

type SelectRecipientsCollectedDataLibraryProps = {
  updateSelectType: (selectType: Partial<SelectRecipientGroupType>) => void;
};

const SelectRecipientsCollectedDataLibrary = (
  props: SelectRecipientsCollectedDataLibraryProps
) => {
  const { updateSelectType } = props;
  const { savedQuestions } = useSelector(getAccountState).account;
  const [searchTerm, setSearchTerm] = useState("");
  const [formQuestions, setFormQuestions] = useState<SavedFormQuestion[]>([]);
  const [stateValue, setStateValue] = useState(0);
  const [filterState, setFilterState] = useState(
    DataQuestionFilterType.ANY_TYPE
  );

  // we don't use selectedFormQuestions as a dependency so that it doesn't re-order while you're pressing options
  useEffect(() => {
    const finalToShow = sortSavedQuestions(savedQuestions, [], []);
    setFormQuestions(finalToShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQuestions]);

  const handleSearch = (text: string) => {
    setSearchTerm(text);
  };

  const contains = useCallback((question: SavedFormQuestion, query: string) => {
    const label = question.formInput.label ?? "";
    return label.toLowerCase().includes(query);
  }, []);

  const itemsToShow = useMemo(() => {
    let questions = getSavedFormQuestionsToDisplay(
      formQuestions,
      stateValue === 0
        ? SavedFormQuestionState.ACTIVE
        : SavedFormQuestionState.INACTIVE,
      filterState
    );
    if (searchTerm !== "") {
      questions = filter(questions, (question: SavedFormQuestion) => {
        return contains(question, searchTerm);
      });
    }
    return questions;
  }, [contains, filterState, formQuestions, searchTerm, stateValue]);

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 14, paddingBottom: 10 }}>
        <span style={{ fontWeight: "500" }}>Select by Collected Data</span>
        <div className="AlignedRow" style={{ gap: 10 }}>
          <SearchBoxContainer
            value={searchTerm}
            placeholder={"Search..."}
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
            containerStyles={{ marginTop: 0 }}
          />
          <SavedQuestionTypeDropdown
            selectedType={filterState}
            setSelectedType={setFilterState}
          />
        </div>
        <ListTabs
          tabLabels={["Active", "Inactive"]}
          tabNumbers={[-1, -1]}
          selectedValue={stateValue}
          onChange={setStateValue}
          altColor={Colors.BLACK}
        />
      </div>
      <div
        className="AllowScroll"
        style={{ paddingBottom: 200, paddingTop: 14 }}
      >
        <FlatList
          list={itemsToShow}
          renderItem={(item: SavedFormQuestion) => (
            <div>
              <SavedQuestionItem
                item={item}
                onItemPress={() => updateSelectType({ selectedItem: item })}
                rightIcon={
                  <Icon
                    icon="mdi:chevron-right"
                    height={22}
                    style={{ color: Colors.GRAY1, cursor: "pointer" }}
                  />
                }
              />
              <hr />
            </div>
          )}
          renderWhenEmpty={() => (
            <EmptyStateFlatlist
              containerStyles={{ paddingTop: 200 }}
              isLoading={false}
              searchTerm={searchTerm}
              nonSearchEmptyView={
                <EmptyStateButton
                  title="No Collected Data"
                  description="Your saved questions will show up here"
                  icon={
                    <Icon
                      icon={"ion:documents"}
                      height={64}
                      style={{ color: Colors.GRAY1 }}
                    />
                  }
                  iconBox={84}
                  containerStyles={{ paddingTop: 100 }}
                />
              }
            />
          )}
        />
      </div>
    </>
  );
};

export default SelectRecipientsCollectedDataLibrary;
